import { CdkDrag, CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Location } from '@angular/common';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { BetterBuysService } from '@app/better-buys/services/better-buys.service';
import { ModalController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { OrderService } from '@order/services/order.service';
import { PanAppState, PlatformService, Tracking } from '@panamax/app-state';
import { Nutrient } from '@product-detail/models/nutrient.model';
import { ProductDetailViewModel } from '@product-detail/models/product-detail-view-model';
import { ProductInventoryService } from '@product-detail/services/product-inventory.service';
import { formatDate } from '@product-detail/utils/date.util';
import { ProductExceptionPopoverComponent } from '@search/components/product-exception-popover/product-exception-popover.component';
import { ProductComparisonService } from '@search/services/product-compare.service';
import { DetailHistoryService } from '@shared/components/header/history/detail-history.service';
import { PageTitle } from '@shared/constants/page-titles.enum';
import { PATHS } from '@shared/constants/paths';
import { ProductCompareEnum } from '@shared/constants/product-compare.enum';
import { FEATURES } from '@shared/constants/splitio-features';
import { NavigationHelperService } from '@shared/helpers/navigation.helpers.service';
import { Product } from '@shared/models/product.model';
import { QuantityUpdate } from '@shared/models/quantity-update';
import { ModalService } from '@shared/services/modal/modal.service';
import { OrderItemService } from '@shared/services/order-item.service';
import { ListState } from '@usf/ngrx-list';
import { ProductPropertiesEnum } from '@usf/product-types/Product-Summary';
import dayjs from 'dayjs';
import { combineLatest, firstValueFrom, Subscription } from 'rxjs';
import { filter, switchMap, take } from 'rxjs/operators';

@Component({
  selector: 'app-product-comparison',
  templateUrl: './product-comparison.component.html',
  styleUrls: ['./product-comparison.component.scss'],
})
export class ProductComparisonComponent implements OnInit, OnDestroy {
  queryParam$: Subscription;
  params: Params;
  selectedProducts: number[];
  productsDetails: ProductDetailViewModel[];
  productDetailsMap: Map<number, ProductDetailViewModel>;
  inventoryCSMessageMap: Map<number, string>;
  inventoryEAMessageMap: Map<number, string>;
  originalItemNumber: number;

  productsDetails$: Subscription;
  featureFlagSub$: Subscription;
  pricingDisabled: boolean;

  @Input() shouldConvertToPopover: boolean;
  @Input() isGuestUser = false;

  readonly propertiesEnum = ProductPropertiesEnum;
  listState: ListState;
  mode = ProductCompareEnum.default;

  ProductCompareEnum = ProductCompareEnum;
  printFeatureFlag = FEATURES.split_global_product_compare_print.name;
  downloadFeatureFlag = FEATURES.split_global_product_compare_download.name;
  shareFeatureFlag = FEATURES.split_global_product_compare_share.name;
  isMobile =
    this.platformService.platformType ===
    this.platformService.platformEnum.mobile;
  isTablet =
    this.platformService.platformType ===
    this.platformService.platformEnum.tablet;
  isPrintButtonEnabled: boolean;
  isDownloadButtonEnabled: boolean;
  isShareButtonEnabled: boolean;
  isUsdaNutritionFlagOn: boolean;
  extraNutrientsLabels = ['Vitamin D', 'Calcium', 'Iron', 'Potassium'];
  extraNutrientsLabelsUsda = ['Vitamin A', 'Vitamin C', 'Calcium', 'Iron'];
  index: any;

  constructor(
    private route: ActivatedRoute,
    readonly platformService: PlatformService,
    protected modalController: ModalController,
    private translateService: TranslateService,
    private location: Location,
    private productComparisonService: ProductComparisonService,
    private orderItemService: OrderItemService,
    readonly panAppState: PanAppState,
    private productInventoryService: ProductInventoryService,
    private router: Router,
    private navigationHelperService: NavigationHelperService,
    private modalService: ModalService,
    private betterBuysService: BetterBuysService,
    readonly orderService: OrderService,
    private historyService: DetailHistoryService,
  ) {}

  ngOnInit(): void {
    this.listState = history?.state?.list;
    this.panAppState.setPageTitle(
      this.translateService.instant(PageTitle.searchCompare),
    );

    this.queryParam$ = this.route.queryParams.subscribe(async params => {
      this.params = params;

      this.selectedProducts = this.parseNumberArrayParam(
        this.params.selectedProducts,
      );
      await this.loadProducts(this.selectedProducts);
      this.originalItemNumber = parseInt(params.originalItem);
      this.setPageMode(params);
    });

    this.featureFlagSub$ = combineLatest([
      this.panAppState.feature$([this.printFeatureFlag]),
      this.panAppState.feature$([this.downloadFeatureFlag]),
      this.panAppState.feature$([this.shareFeatureFlag]),
    ]).subscribe(
      ([
        isPrintButtonEnabled,
        isDownloadButtonEnabled,
        isShareButtonEnabled,
      ]) => {
        this.isPrintButtonEnabled = isPrintButtonEnabled;
        this.isDownloadButtonEnabled = isDownloadButtonEnabled;
        this.isShareButtonEnabled = isShareButtonEnabled;
      },
    );
  }

  async loadProducts(productNumbers: number[]) {
    const productDetailsMap = await firstValueFrom(
      this.productComparisonService
        .getProductDetails(productNumbers, true)
        .pipe(
          filter(productDetailsMap =>
            this.hasProductDetailLoaded(productDetailsMap),
          ),
        ),
    );

    this.productDetailsMap = productDetailsMap;
    this.productsDetails = [...productDetailsMap.values()];
    if (!!this.productsDetails) {
      this.productComparisonService.trackProductComparePageLoad(
        this.productsDetails,
      );
      this.productComparisonService.trackProductCompareEvent(
        this.productsDetails,
      );
    }

    this.inventoryCSMessageMap = new Map<number, string>();
    this.inventoryEAMessageMap = new Map<number, string>();

    this.productDetailsMap.forEach((product, productNumber) => {
      this.inventoryCSMessageMap.set(
        productNumber,
        this.getInventoryMessage(product, false),
      );
      this.inventoryEAMessageMap.set(
        productNumber,
        this.getInventoryMessage(product, true),
      );
    });
  }

  private hasProductDetailLoaded(
    productDetailsMap: Map<number, ProductDetailViewModel>,
  ): boolean {
    if (!productDetailsMap) {
      return false;
    }

    for (const value of productDetailsMap.values()) {
      if (!value) {
        return false;
      }
    }

    return true;
  }

  redirectToSearch() {
    this.selectedProducts = null;
    this.location.back();
  }

  setPageMode(params) {
    this.mode = ProductCompareEnum[params.mode]
      ? ProductCompareEnum[params.mode]
      : ProductCompareEnum.default;
  }

  parseNumberArrayParam(param: string): number[] {
    const inputValue = param ?? '';
    const numbersArray = inputValue
      .split(',')
      .map(str => parseInt(str.trim()))
      .filter(num => !isNaN(num));
    if (numbersArray.length === 0) {
      console.error('Invalid parameter: ' + inputValue);
      return [];
    } else {
      return numbersArray;
    }
  }

  onQuantityUpdate(quantityUpdate: QuantityUpdate) {
    // TODO Hook tracking

    const trackingData: Tracking =
      this.productComparisonService.extractChangeQuantityTrackingData(
        quantityUpdate,
        this.productsDetails,
      );

    this.orderItemService.changeQuantity(
      quantityUpdate.productNumber,
      quantityUpdate.quantity,
      quantityUpdate.isEaches,
      trackingData,
    );
  }

  onCloseCard(productDetail: ProductDetailViewModel) {
    this.productsDetails.forEach((item, index) => {
      if (item === productDetail) this.productsDetails.splice(index, 1);
    });
    const selectedProducts = this.params.selectedProducts?.split(',');
    const updatedSelectedProducts = selectedProducts?.filter(
      productNumber => productNumber !== productDetail.productNumber.toString(),
    );
    const queryParams = {
      ...this.params,
      selectedProducts: updatedSelectedProducts?.join(','),
    };

    this.router.navigate([], { queryParams, replaceUrl: true });
  }

  getInventoryMessage(product: ProductDetailViewModel, displayEaches: boolean) {
    const productProperties = product?.summary?.properties;
    const productInventory = product?.inventory;

    let inventoryMessage: string;

    if (displayEaches && !product?.summary?.breakable) {
      inventoryMessage = 'N/A';
    } else if (
      productProperties?.has(this.propertiesEnum.supplierOutOfStock) ||
      !productInventory?.isInStock
    ) {
      inventoryMessage = 'i18n.productDetail.notAvailable';
    } else if (
      productProperties?.has(this.propertiesEnum.direct) ||
      productProperties?.has(this.propertiesEnum.jit) ||
      productProperties?.has(this.propertiesEnum.ces) ||
      productProperties?.has(this.propertiesEnum.specialOrder)
    ) {
      inventoryMessage = 'i18n.productDetail.available';
    } else {
      const inventoryValue =
        this.productInventoryService.getInventoryValueFormat(
          productInventory,
          displayEaches,
        );

      if (displayEaches && (!inventoryValue || inventoryValue === '0')) {
        inventoryMessage = 'i18n.productDetail.available';
      } else {
        inventoryMessage = inventoryValue;
      }
    }

    return this.formatInventoryMessage(inventoryMessage, displayEaches);
  }

  private formatInventoryMessage(
    inventoryMessage: string,
    displayEaches: boolean,
  ) {
    let inventoryMessageFormat: string;

    if (
      inventoryMessage === 'N/A' ||
      inventoryMessage === 'i18n.productDetail.available'
    ) {
      inventoryMessageFormat = inventoryMessage;
    } else if (
      inventoryMessage === '0' ||
      inventoryMessage === 'i18n.productDetail.notAvailable'
    ) {
      inventoryMessageFormat = 'i18n.search.searchCompare.outOfStock';
    } else if (!displayEaches) {
      inventoryMessageFormat = `${inventoryMessage} CS`;
    } else {
      inventoryMessageFormat = `${inventoryMessage} EA`;
    }

    return inventoryMessageFormat;
  }

  getPurchaseHistory(product: ProductDetailViewModel): string[] {
    let purchaseHistory: string[] = [];
    const format = 'MM/DD/YYYY';
    for (let i = 1; i <= 4 && product?.recentPurchase; i++) {
      let row;
      if (!!product?.recentPurchase[`purchaseDtm${i}`]) {
        const purchaseDate = dayjs(product?.recentPurchase[`purchaseDtm${i}`]);
        row =
          formatDate(format, purchaseDate) +
          ' - ' +
          (product?.recentPurchase[`unitsOrdered${i}`]
            ? product?.recentPurchase[`unitsOrdered${i}`]
            : '0') +
          ' CS, ' +
          (product?.recentPurchase[`eachesOrdered$${i}`]
            ? product?.recentPurchase[`eachesOrdered$${i}`]
            : '0') +
          ' EA';
        purchaseHistory.push(row);
      }
    }
    return purchaseHistory.length > 0 ? purchaseHistory : ['N/A'];
  }

  ngOnDestroy(): void {
    this.queryParam$?.unsubscribe();
    this.productsDetails$?.unsubscribe();
    this.featureFlagSub$?.unsubscribe();
  }

  trackByProductNumber(index: number, productDetail: ProductDetailViewModel) {
    return productDetail.productNumber;
  }

  drop(event: CdkDragDrop<ProductDetailViewModel[]>) {
    moveItemInArray(
      this.productsDetails,
      event.previousIndex,
      event.currentIndex,
    );
  }

  sortPredicate(index: number, item: CdkDrag<ProductDetailViewModel>) {
    return index !== 0;
  }

  noOpSortPredicate(index: number, item: CdkDrag<ProductDetailViewModel>) {
    return true;
  }

  selectOrderSubstitute(productNumber: number) {
    const productToBeReplaced = this.productDetailsMap.get(
      this.originalItemNumber,
    );
    const selectedReplacementProduct =
      this.productDetailsMap.get(productNumber);

    if (productToBeReplaced && selectedReplacementProduct) {
      this.orderItemService.substituteProducts(
        productToBeReplaced,
        selectedReplacementProduct,
      );
      this.navigationHelperService.routeTo(PATHS.ORDER);
    }
  }

  public get sectionToGoBack() {
    let goBackMessage;
    switch (this.mode) {
      case ProductCompareEnum.betterBuyCompare:
        goBackMessage = 'i18n.search.searchCompare.backBetterBuys';
        break;
      case ProductCompareEnum.exceptionSubCompare:
        goBackMessage = !this.historyService
          .getPreviousUrl()
          ?.toLowerCase()
          .includes('search')
          ? 'i18n.orderExceptionsPage.resolveExceptions'
          : 'i18n.search.searchCompare.backSearchResult';
        break;
      case ProductCompareEnum.reviewOrder:
        goBackMessage = 'i18n.lists.reviewOrder';
        break;
      case ProductCompareEnum.betterBuyListsCompare:
      case ProductCompareEnum.listsDetailCompare:
      case ProductCompareEnum.replacementListsCompare:
        goBackMessage = 'i18n.lists.listDetails';
        break;
      case ProductCompareEnum.didYouForgetCompare:
        goBackMessage = 'i18n.pageTitles.didYouForget';
        break;
      case ProductCompareEnum.topSellerCompare:
        goBackMessage = 'i18n.pageTitles.topSellers';
        break;
      case ProductCompareEnum.trendingNowCompare:
        goBackMessage = 'i18n.pageTitles.trendingNow';
        break;
      case ProductCompareEnum.masterListCompare:
        goBackMessage = 'i18n.breadcrumbs.masterList';
        break;
      default:
        goBackMessage = 'i18n.search.searchCompare.backSearchResult';
        break;
    }
    return this.translateService.instant(goBackMessage);
  }

  async selectExceptionSubstitute(productNumber: number) {
    const exceptionId = this.params.exceptionId;
    const orderId = this.params.orderId;
    const productToBeReplaced = this.productDetailsMap.get(
      this.originalItemNumber,
    );
    const selectedReplacementProduct =
      this.productDetailsMap.get(productNumber);

    this.modalService.setModalOptions(
      this.platformService.isTouch.value,
      ProductExceptionPopoverComponent,
      {
        product: productToBeReplaced,
        replacementProduct: selectedReplacementProduct,
        quantityEach: {
          previousValue: 0,
          currentValue: 0,
          loading: false,
          error: null,
        },
        quantityUnit: {
          previousValue: 0,
          currentValue: 0,
          loading: false,
          error: null,
        },
        exceptionId,
        mode: this.mode,
      },
      'modal-desktop-size-lg',
    );

    const modal = await this.modalController.create(
      this.modalService.modalOptions,
    );

    modal.onDidDismiss().then(payload => {
      if (payload.data?.success) {
        this.router.navigate(['order', 'order-exceptions', orderId]);
      }
    });
    await modal.present();
  }

  acceptSwap(productNumber: number) {
    const product = this.productDetailsMap.get(productNumber) as Product;
    if (!!product) {
      this.betterBuysService.openAcceptSwapModal(product, true);
    }
  }
  onPrintButtonPress() {
    this.productComparisonService.printProductCompare(this.productsDetails);
  }
  onDownloadButtonPress() {
    this.productComparisonService.downloadProductCompare(this.productsDetails);
  }
  onShareButtonPress() {
    this.productComparisonService.shareProductCompare(this.productsDetails);
  }
  ellipsisClick(event: any) {
    const buttons = this.productComparisonService.constructEllipsisButtons(
      event,
      this.productsDetails,
      this.isPrintButtonEnabled,
      this.isDownloadButtonEnabled,
      this.isShareButtonEnabled,
    );
    this.productComparisonService.openProductCompareEllipsis(buttons);
  }

  getNutrientByName(product: ProductDetailViewModel, label: string): Nutrient {
    return product?.nutritionFacts?.nutrientsData?.find(
      x => x.data?.nutrientName === label,
    );
  }
}
